import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    accepted_terms: false,
    accepted_privacy: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('La password deve contenere almeno 8 caratteri');
      return;
    }

    if (!formData.accepted_terms || !formData.accepted_privacy) {
      toast.error('Devi accettare i Termini e la Privacy Policy per registrarti');
      return;
    }

    setLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        accepted_terms: formData.accepted_terms,
        accepted_privacy: formData.accepted_privacy,
      });
      
      await login(formData.email, formData.password);
      toast.success('Account creato con successo');
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la registrazione';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 rounded-xl shadow-lg border border-border">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <span className="font-heading text-3xl font-bold text-primary">DalSud</span>
            </Link>
            <h1 className="mt-4 font-heading text-2xl font-bold text-text-primary">
              Crea il tuo account
            </h1>
            <p className="mt-2 text-text-secondary">
              Hai già un account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Accedi
              </Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Nome</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Mario"
                  className="mt-1"
                  data-testid="register-firstname"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Cognome</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Rossi"
                  className="mt-1"
                  data-testid="register-lastname"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="mario@esempio.it"
                required
                className="mt-1"
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minimo 8 caratteri"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Conferma Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Ripeti la password"
                required
                className="mt-1"
                data-testid="register-confirm-password"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 flex-shrink-0"
                  checked={formData.accepted_terms}
                  onChange={e => setFormData({ ...formData, accepted_terms: e.target.checked })}
                  data-testid="register-accept-terms"
                />
                <span className="text-sm text-text-secondary">
                  Accetto i{' '}
                  <Link to="/legal/termini-condizioni" target="_blank" className="text-primary underline">
                    Termini e Condizioni
                  </Link>
                  {' '}*
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 flex-shrink-0"
                  checked={formData.accepted_privacy}
                  onChange={e => setFormData({ ...formData, accepted_privacy: e.target.checked })}
                  data-testid="register-accept-privacy"
                />
                <span className="text-sm text-text-secondary">
                  Ho letto la{' '}
                  <Link to="/legal/privacy-policy" target="_blank" className="text-primary underline">
                    Privacy Policy
                  </Link>
                  {' '}*
                </span>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !formData.accepted_terms || !formData.accepted_privacy}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registrazione in corso...
                </>
              ) : (
                'Crea Account'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
